import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface BulkApplyMaintenanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  toCreateCount: number;
  toRemoveCount: number;
  templateName: string;
  onConfirm: (nextDueDate: string) => void;
  onSkip: () => void;
  isApplying: boolean;
}

export const BulkApplyMaintenanceDialog: React.FC<BulkApplyMaintenanceDialogProps> = ({
  open,
  onOpenChange,
  toCreateCount,
  toRemoveCount,
  templateName,
  onConfirm,
  onSkip,
  isApplying,
}) => {
  const [nextDueDate, setNextDueDate] = useState('');

  const handleConfirm = () => {
    if (!nextDueDate && toCreateCount > 0) return;
    onConfirm(nextDueDate);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setNextDueDate('');
    }
    onOpenChange(isOpen);
  };

  const hasCreates = toCreateCount > 0;
  const hasRemoves = toRemoveCount > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Onderhoudsschema&apos;s bijwerken</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              <p>
                De regel &apos;{templateName}&apos; is gewijzigd. De volgende acties kunnen worden
                uitgevoerd:
              </p>
              {hasRemoves && (
                <p>
                  <strong>{toRemoveCount}</strong> bestaande schema&apos;s worden{' '}
                  <strong className="text-destructive">verwijderd</strong> (voorzieningen buiten het segment).
                </p>
              )}
              {hasCreates && (
                <p>
                  <strong>{toCreateCount}</strong> nieuwe schema&apos;s worden{' '}
                  <strong className="text-emerald-600">aangemaakt</strong> (voorzieningen in het segment zonder schema).
                </p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {hasCreates && (
          <div className="py-2">
            <Label htmlFor="nextDueDate" className="text-sm font-medium mb-1.5 block">
              Volgende onderhoudsdatum
            </Label>
            <Input
              id="nextDueDate"
              type="date"
              value={nextDueDate}
              onChange={(e) => setNextDueDate(e.target.value)}
              className="w-full"
            />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onSkip}
            disabled={isApplying}
          >
            Alleen voor nieuwe
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isApplying || (hasCreates && !nextDueDate)}
          >
            {isApplying ? 'Bezig...' : 'Toepassen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
